import { useEffect, useState } from 'react';

import { MEDIA_QUERIES } from 'components/constants/breakpoints';

/**
 * Минимум, который нужен от `MediaQueryList`. Старый Safari (< 14) знает
 * только `addListener`, а заглушки в тестах — бывает, что ни того ни другого.
 */
type TMediaList = {
  matches: boolean;
  addEventListener?: (
    type: 'change',
    cb: (e: { matches: boolean }) => void,
  ) => void;
  removeEventListener?: (
    type: 'change',
    cb: (e: { matches: boolean }) => void,
  ) => void;
  addListener?: (cb: (e: { matches: boolean }) => void) => void;
  removeListener?: (cb: (e: { matches: boolean }) => void) => void;
};

const getList = (query: string): TMediaList | null => {
  if (typeof window === 'undefined') return null;
  if (typeof window.matchMedia !== 'function') return null;
  return (window.matchMedia(query) as unknown) as TMediaList | null;
};

/**
 * Подписка на медиазапрос (ТЗ 0121, порт из LMS
 * `tmslms_client/src/services/hooks/useMediaQuery.ts`).
 *
 * ⚠️ Именно `matchMedia`, а не `resize`: событие приходит один раз — в момент
 * пересечения границы, а не на каждом кадре перетаскивания края окна.
 *
 * ⚠️ В jsdom `matchMedia` нет вовсе — хук отвечает `false`. Значит в тестах
 * по умолчанию компьютерный режим (не телефон, не сенсор): существующие тесты
 * не меняются. Телефон в тесте — `mockMatchMedia` из `mock/matchMedia`.
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() =>
    Boolean(getList(query)?.matches),
  );

  useEffect(() => {
    const list = getList(query);
    if (!list) return undefined;
    const onChange = (event: { matches: boolean }) => setMatches(event.matches);
    setMatches(Boolean(list.matches));
    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', onChange);
      return () => list.removeEventListener?.('change', onChange);
    }
    if (typeof list.addListener === 'function') {
      list.addListener(onChange);
      return () => list.removeListener?.(onChange);
    }
    return undefined;
  }, [query]);

  return matches;
};

/** Телефон: ширина ≤ 767 px. Раскладка — по ширине. */
export const useIsMobile = (): boolean => useMediaQuery(MEDIA_QUERIES.mobile);

/**
 * Сенсорный экран без наведения — `(hover: none)`. Поведение (нажатие вместо
 * наведения), а не раскладка: планшет в альбомной ориентации — компьютерная
 * раскладка, но наведения нет.
 */
export const useIsTouch = (): boolean => useMediaQuery(MEDIA_QUERIES.touch);
